import {
  Attribute,
  BelongsTo,
  HasMany,
  JsonApiModel,
  JsonApiModelConfig,
} from '@elearnio/angular2-jsonapi';

import { CourseCategory } from '@models/course-category.model';
import { Member } from '@models/member.model';
import { LearningPathAssignment } from '@models/learning-path-assignment.model';
// tslint:disable-next-line:max-line-length
import { LearningPathCourseSection } from '@models/learning-path/learning-path-course-section.model';
import { Course } from './course.model';
import { ILTCourse } from './ilt-course.model';
import { LearningPathMemberResult } from './learning-path/member/learning-path-member-result.model';
import { LearningPathReminder } from '@models/learning-path/learning-path-reminder.model';
import { OrganizationLevelLearningPath } from './organization/organization-level-learning-path.model';
import { VisibilityPermission } from './organization/visibility-permission.model';
import { LearningPathSectionLabel } from './learning-path/learning-path-section-label.model';
import { TrainingAvailability } from '@core/constants/training-status.constants';
import { TrainingTag } from '@models/company/training-tag.model';
import { TrainingTranslation } from '@models/training-translation.model';

@JsonApiModelConfig({
  type: 'learning_paths',
})
export class LearningPath extends JsonApiModel {
  @Attribute()
  description!: string;

  @Attribute({ serializedName: 'estimated_time_in_minutes' })
  estimatedTimeInMinutes!: number;

  @Attribute({ serializedName: 'net_time_in_minutes' })
  netTimeInMinutes!: number;

  @Attribute()
  language!: string;

  @Attribute()
  state!: TrainingAvailability;

  @Attribute()
  name!: string;

  @Attribute({ serializedName: 'certificate_template_id' })
  certificateId!: number | null;

  @Attribute()
  deadline!: Date;

  @Attribute({ serializedName: 'time_limit' })
  timeLimit!: number;

  @Attribute({ serializedName: 'time_limit_unit' })
  timeLimitUnit!: string;

  @Attribute({ serializedName: 'course_count' })
  courseCount!: number;

  @Attribute({ serializedName: 'gamification_points' })
  gamificationPoints!: number;

  @Attribute({ serializedName: 'total_gamification_points' })
  totalGamificationPoints!: number;

  @Attribute({ serializedName: 'free_courses_order' })
  freeCoursesOrder!: boolean;

  @Attribute()
  mandatory!: boolean;

  @Attribute({ serializedName: 'created_at' })
  createdAt!: Date;

  @Attribute({ serializedName: 'updated_at' })
  updatedAt!: Date;

  @Attribute({ serializedName: 'cover_image' })
  coverImage!: string | null;

  @Attribute({ serializedName: 'self_assignment' })
  selfAssignment!: boolean;

  @Attribute({ serializedName: 'training_editors_count' })
  trainingEditorCount!: number;

  @Attribute({ serializedName: 'author_id' })
  authorId!: string;

  @Attribute({ serializedName: 'self_assignment_approval' })
  selfAssignmentApproval!: boolean;

  @Attribute({ serializedName: 'self_assignment_approval_group_leader' })
  selfAssignmentApprovalGroupLeader!: boolean;

  @Attribute({ serializedName: 'self_assignment_approval_manager' })
  selfAssignmentApprovalManager!: boolean;

  @Attribute({ serializedName: 'self_assignment_approval_author' })
  selfAssignmentApprovalAuthor!: boolean;

  @Attribute({ serializedName: 'publishing_approval' })
  publishingApproval!: boolean;

  @Attribute({ serializedName: 'publishing_approval_admin' })
  publishingApprovalAdmin!: boolean;

  @Attribute({ serializedName: 'publishing_approval_global_coach' })
  publishingApprovalGlobalCoach!: boolean;

  @Attribute({ serializedName: 'publishing_approval_author' })
  publishingApprovalAuthor!: boolean;

  @Attribute({ serializedName: 'publishing_approval_editors' })
  publishingApprovalEditors!: boolean;

  @Attribute({ serializedName: 'reviewer_user_ids' })
  reviewerUserIds!: string[];

  // Only returned on the single-training (settings) fetch — see LearningPathResource.
  @Attribute({ serializedName: 'can_review', readonly: true })
  canReview!: boolean;

  @Attribute({ serializedName: 'translation_languages' })
  translationLanguages!: string[];

  @BelongsTo()
  author!: Member;

  @HasMany({ key: 'course_categories' })
  courseCategories!: CourseCategory[];

  @HasMany({ key: 'tags' })
  trainingTags!: TrainingTag[];

  @HasMany({ key: 'ilt_courses' })
  iltCourses!: ILTCourse[];

  @HasMany({ key: 'courses' })
  courses!: Course[];

  @HasMany({ key: 'learning_path_assignments' })
  learningPathAssignments!: LearningPathAssignment[];

  @HasMany({ key: 'learning_path_courses_sections' })
  learningPathCourseSections!: LearningPathCourseSection[];

  @HasMany({ key: 'learning_path_reminders' })
  reminders!: LearningPathReminder[];

  @HasMany({ key: 'organisation_levels_learning_paths' })
  organizationLevelLP!: OrganizationLevelLearningPath[];

  @HasMany({ key: 'visibility_permissions' })
  visibilityPermissions!: VisibilityPermission[];

  @HasMany({ key: 'editors' })
  editors!: Member[];

  @HasMany({ key: 'training_translations' })
  trainingTranslations!: TrainingTranslation[];

  allCourses!: (Course | ILTCourse | LearningPathSectionLabel)[];

  learningPathResult: LearningPathMemberResult | undefined;
}
